import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PrivateRoute } from './components/PrivateRoute';
import { RoleProtectedRoute } from './components/RoleProtectedRoute';
import { RoleRedirect } from './components/RoleRedirect';
import { AdminRoute } from './components/AdminRoute';
import { HomeScreen } from './pages/HomeScreen';

// Todas as demais páginas são carregadas sob demanda: a landing page pública (rota "/",
// primeiro contato de visitantes não autenticados e alvo de auditorias como o PageSpeed
// Insights) não precisa baixar o código do dashboard, admin, formulários etc. Isso reduz
// drasticamente o JS "unused" no carregamento inicial (~80% do bundle antes disso).
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Cadastro = lazy(() => import('./pages/Cadastro').then(m => ({ default: m.Cadastro })));
const FeedVagas = lazy(() => import('./pages/FeedVagas').then(m => ({ default: m.FeedVagas })));
const EmpresaDashboard = lazy(() => import('./pages/EmpresaDashboard').then(m => ({ default: m.EmpresaDashboard })));
const CandidatosVaga = lazy(() => import('./pages/CandidatosVaga').then(m => ({ default: m.CandidatosVaga })));
const PerfilEmpresa = lazy(() => import('./pages/PerfilEmpresa').then(m => ({ default: m.PerfilEmpresa })));
const PerfilProfissional = lazy(() => import('./pages/PerfilProfissional').then(m => ({ default: m.PerfilProfissional })));
const VagasSalvas = lazy(() => import('./pages/VagasSalvas').then(m => ({ default: m.VagasSalvas })));
const DetalhesVaga = lazy(() => import('./pages/DetalhesVaga').then(m => ({ default: m.DetalhesVaga })));
const MinhasVagas = lazy(() => import('./pages/MinhasVagas').then(m => ({ default: m.MinhasVagas })));
const NovaVaga = lazy(() => import('./pages/NovaVaga').then(m => ({ default: m.NovaVaga })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const ListaEmpresas = lazy(() => import('./pages/ListaEmpresas').then(m => ({ default: m.ListaEmpresas })));
const ListaProfissionais = lazy(() => import('./pages/ListaProfissionais').then(m => ({ default: m.ListaProfissionais })));
const MinhasCandidaturas = lazy(() => import('./pages/MinhasCandidaturas').then(m => ({ default: m.MinhasCandidaturas })));
const EditarVaga = lazy(() => import('./pages/EditarVaga').then(m => ({ default: m.EditarVaga })));
const Times = lazy(() => import('./pages/Times').then(m => ({ default: m.Times })));
const TimesEmpresa = lazy(() => import('./pages/TimesEmpresa').then(m => ({ default: m.TimesEmpresa })));
const CriarTime = lazy(() => import('./pages/CriarTime').then(m => ({ default: m.CriarTime })));
const EditarTime = lazy(() => import('./pages/EditarTime').then(m => ({ default: m.EditarTime })));
const Substituicoes = lazy(() => import('./pages/Substituicoes').then(m => ({ default: m.Substituicoes })));
const DetalhesSubstituicao = lazy(() => import('./pages/DetalhesSubstituicao').then(m => ({ default: m.DetalhesSubstituicao })));
const NovaSubstituicao = lazy(() => import('./pages/NovaSubstituicao').then(m => ({ default: m.NovaSubstituicao })));
const EditarSubstituicao = lazy(() => import('./pages/EditarSubstituicao').then(m => ({ default: m.EditarSubstituicao })));
const TimeDetalhes = lazy(() => import('./pages/TimeDetalhes').then(m => ({ default: m.TimeDetalhes })));
const DetalhesProfissional = lazy(() => import('./pages/DetalhesProfissional').then(m => ({ default: m.DetalhesProfissional })));
const DetalhesEmpresa = lazy(() => import('./pages/DetalhesEmpresa').then(m => ({ default: m.DetalhesEmpresa })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade').then(m => ({ default: m.PoliticaPrivacidade })));
const MinhaAgenda = lazy(() => import('./pages/MinhaAgenda').then(m => ({ default: m.MinhaAgenda })));
const Avaliacoes = lazy(() => import('./pages/Avaliacoes').then(m => ({ default: m.Avaliacoes })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AcessoNegado = lazy(() => import('./pages/AcessoNegado').then(m => ({ default: m.AcessoNegado })));

const CarregandoPagina = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent border-t-brand-500 border-r-cyan-500" />
  </div>
);

function App() {
  return (
    <div className="App">
      <Suspense fallback={<CarregandoPagina />}>
      <Routes>
        <Route path="/" element={<HomeScreen />} />
        <Route path="/login" element={<Login />} />
        <Route path="/cadastro" element={<Cadastro />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/privacidade" element={<PoliticaPrivacidade />} />
        <Route
          path="/times"
          element={
            <RoleRedirect deny="empresa" to="/empresa/times">
              <Times />
            </RoleRedirect>
          }
        />
        <Route path="/times/:id" element={<TimeDetalhes />} />
        <Route
          path="/profissional/:id"
          element={
            <PrivateRoute>
              <DetalhesProfissional />
            </PrivateRoute>
          }
        />
        <Route
          path="/empresa/:id"
          element={
            <PrivateRoute>
              <DetalhesEmpresa />
            </PrivateRoute>
          }
        />

        {/* Rotas de Substituições */}
        <Route path="/substituicoes" element={<Substituicoes />} />
        {/* Pública de propósito, mesmo motivo de /vagas/:id — link de "Compartilhar"
            precisa abrir pra quem não tem conta ainda. */}
        <Route path="/substituicoes/:id" element={<DetalhesSubstituicao />} />
        <Route
          path="/profissional/nova-substituicao"
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="profissional">
                <NovaSubstituicao />
              </RoleProtectedRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/profissional/substituicoes/:id/edit"
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="profissional">
                <EditarSubstituicao />
              </RoleProtectedRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <PrivateRoute>
              <FeedVagas />
            </PrivateRoute>
          }
        />
        <Route
          path="/estagios"
          element={
            <PrivateRoute>
              <FeedVagas estagios />
            </PrivateRoute>
          }
        />
        <Route 
          path="/empresa/dashboard" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <EmpresaDashboard />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          } 
        />
        <Route 
          path="/empresa/vagas/:id/candidatos" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <CandidatosVaga />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/empresa/perfil" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <PerfilEmpresa />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/empresa/minhas-vagas" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <MinhasVagas />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/empresa/nova-vaga" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <NovaVaga />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/empresa/vagas/:id/edit" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <EditarVaga />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/profissional/perfil" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="profissional">
                <PerfilProfissional />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/profissional/vagas-salvas" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="profissional">
                <VagasSalvas />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route 
          path="/profissional/candidaturas" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="profissional">
                <MinhasCandidaturas />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        {/* Pública de propósito: link de "Compartilhar vaga" precisa abrir pra quem não
            tem conta ainda (DetalhesVaga.tsx mostra uma versão enxuta, sem sidebar, com
            botão de candidatar-se que leva pro cadastro). */}
        <Route path="/vagas/:id" element={<DetalhesVaga />} />
        <Route 
          path="/profissional/empresas" 
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="profissional">
                <ListaEmpresas />
              </RoleProtectedRoute>
            </PrivateRoute>
          } 
        />
        <Route
          path="/empresa/profissionais"
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <ListaProfissionais />
              </RoleProtectedRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/empresa/times"
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <TimesEmpresa />
              </RoleProtectedRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/empresa/times/novo"
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <CriarTime />
              </RoleProtectedRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/empresa/times/:id/editar"
          element={
            <PrivateRoute>
              <RoleProtectedRoute allowedRole="empresa">
                <EditarTime />
              </RoleProtectedRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/agenda"
          element={
            <PrivateRoute>
              <MinhaAgenda />
            </PrivateRoute>
          }
        />
        <Route
          path="/avaliacoes"
          element={
            <PrivateRoute>
              <Avaliacoes />
            </PrivateRoute>
          }
        />

        {/* Painel administrativo — acesso restrito à conta admin */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route path="/acesso-negado" element={<AcessoNegado />} />
      </Routes>
      </Suspense>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}

export default App;
